import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Stack,
  alpha,
  useTheme,
  Chip,
  Breadcrumbs,
  Link,
} from "@mui/material";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from "@mui/icons-material/Print";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { fetchCForm, setYear, setMonth } from "../../../store/slices/cFormSlice";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const YEARS = Array.from({ length: 10 }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return { value: year, label: year.toString() };
});

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "0.00";
  return parseFloat(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const splitAmount = (amount) => {
  const value = amount || amount === 0 ? parseFloat(amount) : 0;
  const fixed = value.toFixed(2);
  const [rupees, cents] = fixed.split(".");
  const rupeesWithCommas = parseInt(rupees, 10).toLocaleString("en-US");
  return { rupees: rupeesWithCommas, cents };
};

const PRIMARY = "#1B5C20";
const PRIMARY_DARK = "#0D3B12";

const CFormPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const printRef = useRef();

  const {
    data,
    loading,
    error,
    selectedYear,
    selectedMonth,
    totalEmployer,
    totalEmployee,
    totalEarnings,
    employerName,
    employerAddress,
    epfRegistrationNumber,
    surcharge,
  } = useSelector((state) => state.cForm);

  const [year, setYearLocal] = useState(selectedYear || new Date().getFullYear());
  const [month, setMonthLocal] = useState(selectedMonth || new Date().getMonth() + 1);
  const [printLoading, setPrintLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchCForm({ year: selectedYear || year, month: selectedMonth || month }));
  }, [dispatch, selectedYear, selectedMonth]);

  const handleFetch = () => {
    dispatch(setYear(year));
    dispatch(setMonth(month));
    dispatch(fetchCForm({ year, month }));
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `C_Form_${year}_${String(month).padStart(2, "0")}`,
    onBeforePrint: () => {
      setPrintLoading(true);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setPrintLoading(false);
      toast.success("Print initiated successfully");
    },
    onPrintError: () => {
      setPrintLoading(false);
      toast.error("Failed to print. Please try again.");
    },
  });

  const handleExportPDF = async () => {
    if (data.length === 0) {
      toast.warning("No data to export. Please generate the report first.");
      return;
    }

    try {
      setPdfLoading(true);
      toast.info("Generating PDF...", { autoClose: 1500 });

      const element = printRef.current;
      if (!element) {
        throw new Error("Nothing to export");
      }

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      const canvas = await html2canvas(element, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: element.offsetWidth,
        height: element.offsetHeight,
        windowWidth: element.offsetWidth,
        windowHeight: element.offsetHeight,
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= usableHeight;

      while (heightLeft > 0) {
        position = margin - (imgHeight - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        heightLeft -= usableHeight;
      }

      const fileName = `C_Form_${year}_${String(month).padStart(2, "0")}.pdf`;
      pdf.save(fileName);

      toast.success("PDF downloaded successfully!");
    } catch (err) {
      console.error("PDF Export Error:", err);
      toast.error(`Failed to generate PDF: ${err.message || "Please try again."}`);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadCSV = () => {
    if (data.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const headers = [
        "Employee's Name",
        "National Identity Card No.",
        "Member No.",
        "Total",
        "Employer",
        "Employee",
        "Total Earnings (Rs.)",
      ];

      const rows = data.map((item) => [
        `"${item.EmployeeName || ""}"`,
        `"${item.NationalIdentityCardNo || ""}"`,
        `"${item.MemberNo || ""}"`,
        formatCurrency(item.Total),
        formatCurrency(item.Employer),
        formatCurrency(item.Employee),
        formatCurrency(item.TotalEarnings),
      ]);

      rows.push([
        '"TOTAL"',
        "",
        "",
        formatCurrency(totalEmployer + totalEmployee),
        formatCurrency(totalEmployer),
        formatCurrency(totalEmployee),
        formatCurrency(totalEarnings),
      ]);

      const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `C_Form_${year}_${String(month).padStart(2, "0")}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);

      toast.success("CSV downloaded successfully!");
    } catch (err) {
      console.error("CSV Export Error:", err);
      toast.error("Failed to export CSV");
    }
  };

  const getMonthLabel = (monthValue) => {
    const m = MONTHS.find((mm) => mm.value === monthValue);
    return m ? m.label : "";
  };

  const getMonthShort = (monthValue) => {
    const label = getMonthLabel(monthValue);
    return label ? label.slice(0, 3) : "";
  };

  const contribution = (totalEmployer || 0) + (totalEmployee || 0);
  const surchargeValue = surcharge || 0;
  const totalRemittance = contribution + surchargeValue;

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, bgcolor: "#F8FAF8", minHeight: "100vh" }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" href="/" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <HomeIcon fontSize="small" />
            Home
          </Link>
          <Link underline="hover" color="inherit" href="/payroll" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Payroll
          </Link>
          <Link underline="hover" color="inherit" href="/payroll/reports" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            Reports
          </Link>
          <Typography color="text.primary" fontWeight={600}>
            EPF C Form
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} color={PRIMARY} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountBalanceIcon sx={{ fontSize: 32 }} />
              EPF C Form
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Employee Provident Fund Contribution Report - Form C
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/payroll/reports")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              borderColor: PRIMARY,
              color: PRIMARY,
              "&:hover": { borderColor: PRIMARY_DARK, bgcolor: alpha(PRIMARY, 0.05) },
            }}
          >
            Back to Reports
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{ p: 3, mb: 4, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: "#FFFFFF" }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth label="Year" value={year} onChange={(e) => setYearLocal(parseInt(e.target.value))} size="small">
              {YEARS.map((y) => (
                <MenuItem key={y.value} value={y.value}>
                  {y.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth label="Month" value={month} onChange={(e) => setMonthLocal(parseInt(e.target.value))} size="small">
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={handleFetch}
                disabled={loading}
                sx={{ bgcolor: PRIMARY, "&:hover": { bgcolor: PRIMARY_DARK }, textTransform: "none", borderRadius: 2, px: 3 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Generate Report"}
              </Button>
              {/* <Button
                variant="outlined"
                startIcon={pdfLoading ? <CircularProgress size={20} /> : <PdfIcon />}
                onClick={handleExportPDF}
                disabled={loading || data.length === 0 || pdfLoading}
                sx={{ textTransform: "none", borderRadius: 2, borderColor: "#D32F2F", color: "#D32F2F", "&:hover": { borderColor: "#B71C1C", bgcolor: alpha("#D32F2F", 0.05) } }}
              >
                {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCSV}
                disabled={loading || data.length === 0}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                CSV
              </Button> */}
              <Button
                variant="outlined"
                startIcon={printLoading ? <CircularProgress size={20} /> : <PrintIcon />}
                onClick={handlePrint}
                disabled={loading || data.length === 0 || printLoading}
                sx={{ textTransform: "none", borderRadius: 2 }}
              >
                {printLoading ? "Printing..." : "Print"}
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => {}}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* ========================================================== */}
      {/* PRINT VIEW */}
      {/* ========================================================== */}
      <div ref={printRef} style={{ width: '100%', maxWidth: '210mm', margin: '0 auto' }}>
        <div style={{
          padding: '10px 12px',
          backgroundColor: '#FFFFFF',
          color: '#000000',
          fontFamily: "'Times New Roman', Times, serif",
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
        }}>

          <div style={{ display: 'flex', alignItems: 'stretch', marginBottom: '4px' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '10px', fontWeight: 500 }}>M - 13</span>
            </div>
            <div style={{
              flex: 1.4,
              border: '1px solid #000000',
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              padding: '2px 8px',
              gap: '4px',
            }}>
              <span style={{ fontSize: '18px', lineHeight: 1, fontWeight: 700 }}>C</span>
              <span style={{ fontSize: '8px', letterSpacing: 1, fontWeight: 600 }}>FORM</span>
              <span style={{ fontSize: '7px', marginLeft: '8px', fontWeight: 500 }}>EPF Act No. 15 of 1958</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '4px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '8px', fontWeight: 600, marginBottom: '2px' }}>Employer's Address</div>
              <div style={{ border: '1px solid #000000', minHeight: '120px', padding: '8px' }}>
                <div style={{ fontSize: '10px' }}>
                  {employerName || "AMSK Constructions (Pvt) Ltd."}<br />
                  {employerAddress || "1/29 New Town Madampe, Sri Lanka"}
                </div>
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ height: '14px' }}></div>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '8px',
                tableLayout: 'fixed',
              }}>
                <colgroup>
                  <col style={{ width: '56%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '22%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td colSpan={2} style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '7px' }}>EPF/PPF Registration Number</td>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '9px', fontWeight: 600 }}>{epfRegistrationNumber || "P6912"}</td>
                  </tr>
                  <tr>
                    <td colSpan={2} style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '7px' }}>Year &amp; Month of Contribution</td>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '9px', fontWeight: 600 }}>
                      {getMonthShort(selectedMonth || month)}-{selectedYear || year}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '7px' }}>Contribution</td>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '11px', fontWeight: 700 }}>{splitAmount(contribution).rupees}</td>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '11px', fontWeight: 700 }}>{splitAmount(contribution).cents}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '7px' }}>Surcharge</td>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '11px', fontWeight: 700 }}>{surchargeValue ? splitAmount(surchargeValue).rupees : "\u00A0"}</td>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '11px', fontWeight: 700 }}>{surchargeValue ? splitAmount(surchargeValue).cents : "\u00A0"}</td>
                  </tr>
                  <tr>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '7px', fontWeight: 600 }}>Total Remittance</td>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '11px', fontWeight: 700 }}>{splitAmount(totalRemittance).rupees}</td>
                    <td style={{ border: '1px solid #000000', padding: '2px 3px', textAlign: 'center', fontSize: '11px', fontWeight: 700 }}>{splitAmount(totalRemittance).cents}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '32px', marginBottom: '2px' }}>
            <div style={{ fontSize: '8px' }}>
              <div style={{ fontSize: '8px' }}>Telephone: 2206645</div>
              <div style={{ fontSize: '8px' }}>Fax: 2206645</div>
              <div style={{ fontSize: '8px' }}>Email: epfhelpdesk@cbsl.lk</div>
              <div style={{ fontSize: '8px' }}>Web: www.epf.lk</div>
            </div>
            <div style={{ flex: 1, fontSize: '8px', textAlign: 'right' }}>
              <div style={{ fontSize: '8px' }}>Cheque No. .....................................................................................................</div>
              <div style={{ height: '10px' }}></div>
              <div style={{ fontSize: '8px' }}>Bank Name and Branch Name</div>
              <div style={{ fontSize: '8px' }}>.....................................................................................................</div>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '9px', fontWeight: 500 }}>This form should be returned duly completed along with the contributions to the Superintendent / EPF</span>
          </div>

          {!loading && data.length > 0 && (
            <>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '7px',
              }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', fontWeight: 700, fontSize: '6.5px', padding: '1.5px 2px', border: '1px solid #000000' }}>Employee's Name</th>
                    <th style={{ textAlign: 'left', fontWeight: 700, fontSize: '6.5px', padding: '1.5px 2px', border: '1px solid #000000' }}>National Identity Card No.</th>
                    <th style={{ textAlign: 'left', fontWeight: 700, fontSize: '6.5px', padding: '1.5px 2px', border: '1px solid #000000' }}>Member No.</th>
                    <th style={{ textAlign: 'center', fontWeight: 700, fontSize: '6.5px', padding: '1.5px 2px', border: '1px solid #000000' }}>Total</th>
                    <th style={{ textAlign: 'center', fontWeight: 700, fontSize: '6.5px', padding: '1.5px 2px', border: '1px solid #000000' }}>Employer</th>
                    <th style={{ textAlign: 'center', fontWeight: 700, fontSize: '6.5px', padding: '1.5px 2px', border: '1px solid #000000' }}>Employee</th>
                    <th style={{ textAlign: 'right', fontWeight: 700, fontSize: '6.5px', padding: '1.5px 2px', border: '1px solid #000000' }}>Total Earnings (Rs.)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => {
                    const total = splitAmount(item.Total);
                    const employer = splitAmount(item.Employer);
                    const employee = splitAmount(item.Employee);
                    return (
                      <tr key={index}>
                        <td style={{ fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>{item.EmployeeName || ''}</td>
                        <td style={{ fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>{item.NationalIdentityCardNo || ''}</td>
                        <td style={{ fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>{item.MemberNo || ''}</td>
                        <td style={{ textAlign: 'center', fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>
                          {total.rupees} {total.cents}
                        </td>
                        <td style={{ textAlign: 'center', fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>
                          {employer.rupees} {employer.cents}
                        </td>
                        <td style={{ textAlign: 'center', fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>
                          {employee.rupees} {employee.cents}
                        </td>
                        <td style={{ textAlign: 'right', fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>
                          {formatCurrency(item.TotalEarnings)}
                        </td>
                      </tr>
                    );
                  })}

                  <tr>
                    <td colSpan={3} style={{ fontWeight: 700, textAlign: 'right', fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>Total</td>
                    <td style={{ fontWeight: 700, textAlign: 'center', borderTop: '2px solid #000000', fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>
                      {splitAmount(totalEmployer + totalEmployee).rupees} {splitAmount(totalEmployer + totalEmployee).cents}
                    </td>
                    <td style={{ fontWeight: 700, textAlign: 'center', borderTop: '2px solid #000000', fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>
                      {splitAmount(totalEmployer).rupees} {splitAmount(totalEmployer).cents}
                    </td>
                    <td style={{ fontWeight: 700, textAlign: 'center', borderTop: '2px solid #000000', fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>
                      {splitAmount(totalEmployee).rupees} {splitAmount(totalEmployee).cents}
                    </td>
                    <td style={{ fontWeight: 700, textAlign: 'right', borderTop: '2px solid #000000', fontSize: '6.5px', padding: '1px 2px', border: '1px solid #000000' }}>
                      {formatCurrency(totalEarnings)}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '9px' }}>I certify that the information given above is correct.</div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '8px', gap: '32px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '9px' }}>..........................................................................................................
                    </div>
                    <div style={{ fontSize: '9px', textAlign: 'center' }}>Signature of Employer</div>
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '7px' }}>Please write Employer's EPF Registration Number on the reverse of the cheque.</div>
                    <div style={{ fontSize: '7px', marginTop: '4px' }}>Telephone:</div>
                    <div style={{ fontSize: '7px' }}>Fax:</div>
                    <div style={{ fontSize: '7px' }}>Email:</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} AMSK Constructions (Pvt) Ltd. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default CFormPage;